import { Mail, Phone, Clock, Instagram, Facebook } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { FadeUp, Eyebrow, MaskedLine } from "@/components/Reveal";
import ContactForm from "@/components/forms/ContactForm";
import { CONTACT } from "@/data/products";

const FAQS = [
  {
    q: "How does bespoke pricing work?",
    a: "There are no fixed prices for bespoke Gift Edits — every gift is composed individually. Once we receive your brief, we respond with a curated proposal shaped around your budget. The price is discussed and agreed with you before any payment is taken.",
  },
  {
    q: "What budget do I need for a bespoke gift?",
    a: "Most bespoke edits begin around £50 and scale to whatever the occasion deserves. Tell us your comfortable range in the brief and we'll design within it — beautifully. For high-value concierge commissions, budgets are discussed privately.",
  },
  {
    q: "How long does a Gift Edit take?",
    a: "A standard bespoke edit takes 3–5 working days from an agreed proposal. If your timeline is tighter, tell us in your brief — our Private Concierge regularly handles 24–48 hour turnarounds, depending on sourcing complexity.",
  },
  {
    q: "How do I pay?",
    a: "Collection pieces are paid instantly at our secure card checkout. For bespoke and concierge commissions, we send a secure payment link once your proposal is agreed. We never take payment before the details are settled.",
  },
  {
    q: "Do you deliver?",
    a: "Yes — tracked, insured delivery across the UK, beautifully packaged to arrive as it left us. For urgent concierge requests, same-day courier and hand-delivery can often be arranged within London and the surrounding counties.",
  },
  {
    q: "How do I buy something from The Collection?",
    a: "Instantly, if you like — choose 'Buy now' on any piece for secure card checkout, and your gift ships in our signature wrapping. Prefer to talk first? Choose 'View & Enquire' and we'll confirm availability and arrange everything personally.",
  },
  {
    q: "Can you help with a last-minute gift?",
    a: "Yes — this is a house speciality. Ready-to-gift Collection pieces can often ship next day, and our concierge team handles genuinely urgent sourcing. The sooner you contact us, the more we can do.",
  },
  {
    q: "How do Private Concierge enquiries work?",
    a: "Send a private enquiry through the Concierge page with what you need and when. A member of our team replies personally — usually within hours for urgent requests. We explore legitimate sourcing options, availability and timescales, and respond with an honest proposal. We never guarantee availability of luxury or rare items, and never take payment before a proposal is agreed.",
  },
];

export default function Faqs() {
  return (
    <div data-testid="faqs-page">
      <section className="relative pt-40 pb-16 lg:pb-20 spotlight">
        <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto">
          <FadeUp><Eyebrow>Questions, answered</Eyebrow></FadeUp>
          <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl text-chocolate leading-[1.08] tracking-tight mt-6">
            <MaskedLine delay={0.1}>Everything you'd</MaskedLine>
            <MaskedLine delay={0.25} className="italic text-golddark">like to know.</MaskedLine>
          </h1>
        </div>
      </section>

      <section className="px-4 sm:px-8 lg:px-16 max-w-4xl mx-auto pb-24">
        <FadeUp>
          <Accordion type="single" collapsible data-testid="faq-accordion" className="border-t border-chocolate/10">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} data-testid="faq-accordion-item" className="border-b border-chocolate/10">
                <AccordionTrigger
                  data-testid={`faq-trigger-${i}`}
                  className="font-serif text-xl md:text-2xl font-light text-chocolate hover:text-golddark hover:no-underline py-7 text-left transition-colors"
                >
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm md:text-base text-chocolate/65 leading-relaxed pb-7 max-w-2xl">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </FadeUp>
      </section>

      <section id="contact" className="py-20 lg:py-28 bg-ivory border-t border-chocolate/10 scroll-mt-24">
        <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <FadeUp>
              <Eyebrow>Still curious?</Eyebrow>
              <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-light text-chocolate mt-5 leading-[1.15]">
                Write to us — <span className="italic text-golddark">a person always replies.</span>
              </h2>
              <p className="text-sm text-chocolate/60 leading-relaxed mt-6 max-w-md">
                Whether it's a bespoke brief, a Collection question or something for the concierge, we answer every message personally within one working day.
              </p>
              <ul className="mt-10 space-y-5">
                <li className="flex items-center gap-4">
                  <span className="w-10 h-10 border border-gold/50 flex items-center justify-center text-golddark shrink-0"><Mail size={16} /></span>
                  <a data-testid="contact-email-link" href={`mailto:${CONTACT.email}`} className="text-sm text-chocolate hover:text-golddark transition-colors break-all">{CONTACT.email}</a>
                </li>
                <li className="flex items-center gap-4">
                  <span className="w-10 h-10 border border-gold/50 flex items-center justify-center text-golddark shrink-0"><Phone size={16} /></span>
                  <a data-testid="contact-phone-link" href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} className="text-sm text-chocolate hover:text-golddark transition-colors">{CONTACT.phone}</a>
                </li>
                <li className="flex items-center gap-4">
                  <span className="w-10 h-10 border border-gold/50 flex items-center justify-center text-golddark shrink-0"><Clock size={16} /></span>
                  <span className="text-sm text-chocolate/70">{CONTACT.hours}</span>
                </li>
              </ul>
              <div className="flex gap-3 mt-10">
                <a data-testid="contact-social-instagram" href="https://instagram.com" target="_blank" rel="noopener noreferrer" aria-label="Instagram"
                  className="w-10 h-10 border border-chocolate/15 flex items-center justify-center text-chocolate/60 hover:border-gold hover:text-golddark transition-colors">
                  <Instagram size={16} />
                </a>
                <a data-testid="contact-social-facebook" href="https://facebook.com" target="_blank" rel="noopener noreferrer" aria-label="Facebook"
                  className="w-10 h-10 border border-chocolate/15 flex items-center justify-center text-chocolate/60 hover:border-gold hover:text-golddark transition-colors">
                  <Facebook size={16} />
                </a>
              </div>
            </FadeUp>
          </div>
          <div className="lg:col-span-7">
            <FadeUp delay={0.15}>
              <div className="bg-cream border border-chocolate/10 p-6 sm:p-10">
                <ContactForm />
              </div>
            </FadeUp>
          </div>
        </div>
      </section>
    </div>
  );
}
